import { Button, MuiAlert, Paper, Spin, tryParseJSONObject, Typography, useRenderer } from '@fox/atoms';
import { CreditCardDisplayProps, CreditCardType, PaymentAddProps, paymentExtension, PaymentMethod, ProviderSelection } from '@fox/finance';
import { Flex, FlexColumnSpaced, FlexRowSpaced, Padded } from '@fox/layout';
import { useMediaQuery } from '@mui/material';
import { getComponentExtension, sendUpdate, useSubscribeTopicWithHandler } from 'fox-extensions';
import moment from 'moment';
import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { useContentContext } from '../../../content-context';
import { useFinancialContext } from '../../../financial-config-context';
import { FINANCIAL_DATA_FETCHED, REFETCH_FINANCIAL } from '../../../topics';
import { useUpdateSubscriptionPaymentMethodMutation } from '../../mutation-hooks/mutation-implementations';
import { convertMemberPortalToSfMethodEnumValue } from '../shared';
import { DeskTopOptions } from './desktop-options';
import { MobileOptions } from './mobile-options';
import { SubscriptionSelector } from './subscription-selector';

export const CreditCardDisplay: React.FC<CreditCardDisplayProps> = ({ card, meta }) => {
	const { getCreditCard } = useFinancialContext();
	const useLinkedOrganisationData = meta?.['useLinkedOrganisationData'] === true;
	const creditCard = getCreditCard(card.id as string, useLinkedOrganisationData);
	const { inUse, maskedNumber, type, expires, id } = creditCard;
	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => render());

	const theme = useTheme();
	const now = moment();
	// In the month that the CC expires, the card is still good for that month, so we should be calculating
	// relative to the *end* of the month, not the start.
	const expNow = moment(`${expires.year}-${expires.month}-1`).endOf('month');
	const diff = now.diff(expNow, 'days');
	const isExpired = diff > 0;

	return (
		<Paper style={{ width: '100%' }} key={id}>
			<Padded>
				<FlexColumnSpaced>
					<FlexRowSpaced style={{ justifyContent: 'space-between' }}>
						<div style={{ fontWeight: 'bold' }}>{Object.values(CreditCardType)[type]}</div>
						{inUse ? <span style={{ color: theme.colors.bad }}>In use</span> : null}
					</FlexRowSpaced>
					<FlexRowSpaced style={{ justifyContent: 'space-between' }}>
						<div>
							<span style={{ fontWeight: 'bold' }}>Expiry: </span>
							{`${expires.month}/${expires.year.toString().substring(2)}`}
						</div>
						<div>
							<span style={{ fontWeight: 'bold' }}>Number: </span>
							{maskedNumber}
						</div>
					</FlexRowSpaced>
					{isExpired ? <MuiAlert severity="error">Card is expired!</MuiAlert> : null}
				</FlexColumnSpaced>
			</Padded>
		</Paper>
	);
};

export const CreditCardDisplayWithOptions: React.FC<
	CreditCardDisplayProps & { provider: ProviderSelection; useLinkedOrganisationData: boolean; allowedPlanCategories?: string[] }
> = ({ card, provider, useLinkedOrganisationData, allowedPlanCategories }) => {
	const { getSubscriptions, getCreditCard } = useFinancialContext();
	const subscriptions = getSubscriptions(useLinkedOrganisationData, allowedPlanCategories);
	const creditCard = getCreditCard(card.id as string, useLinkedOrganisationData);
	const { inUse, maskedNumber, type, expires, id } = creditCard;

	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => render());

	const theme = useTheme();
	const isMobile = useMediaQuery('(max-width: 800px)');

	const { requestToken, contentId } = useContentContext();
	const {
		error: updateSubscriptionError,
		loading: updateSubscriptionLoading,
		mutate: updateSubsciption
	} = useUpdateSubscriptionPaymentMethodMutation({ requestToken, topics: [REFETCH_FINANCIAL] });

	const update = async (selectedSubscriptionIds: string[]) => {
		for (const subscriptionId of selectedSubscriptionIds) {
			await updateSubsciption({
				id: subscriptionId,
				contentId,
				method: provider.method,
				provider: provider.provider,
				creditCardId: id as string
			});
		}
	};

	const now = moment();
	// In the month that the CC expires, the card is still good for that month, so we should be calculating
	// relative to the *end* of the month, not the start.
	const expNow = moment(`${expires.year}-${expires.month}-1`).endOf('month');
	const diff = now.diff(expNow, 'days');
	const isExpired = diff > 0;

	const subscriptionsUsingThisCard = subscriptions.filter(s => s.creditCardId === id);

	return (
		<Paper style={{ width: '100%' }} key={id}>
			<Padded>
				<FlexColumnSpaced>
					<FlexRowSpaced style={{ justifyContent: 'space-between' }}>
						<div style={{ fontWeight: 'bold' }}>{Object.values(CreditCardType)[type]}</div>
						{inUse ? <span style={{ color: theme.colors.bad }}>In use</span> : null}
					</FlexRowSpaced>
					<FlexRowSpaced style={{ justifyContent: 'space-between' }}>
						<div>
							<span style={{ fontWeight: 'bold' }}>Expiry: </span>
							{`${expires.month}/${expires.year.toString().substring(2)}`}
						</div>
						<div>
							<span style={{ fontWeight: 'bold' }}>Number: </span>
							{maskedNumber}
						</div>
					</FlexRowSpaced>
					{inUse ? (
						<FlexRowSpaced style={{ flexWrap: 'wrap' }}>
							<span style={{ fontWeight: 'bold', textWrap: 'nowrap' }}>Used by:</span>
							{subscriptionsUsingThisCard.map((s, i) => (
								<Typography key={s.id} style={{ textWrap: 'nowrap', fontStyle: 'italic' }}>
									{s.fee.plan.name}
									{i < subscriptionsUsingThisCard.length - 1 ? ', ' : ''}
								</Typography>
							))}
						</FlexRowSpaced>
					) : null}
					{isExpired ? <MuiAlert severity="error">Card is expired!</MuiAlert> : null}

					<Flex style={{ justifyContent: 'flex-start' }}>
						{isMobile ? (
							<MobileOptions
								id={id}
								paymentMethod={PaymentMethod.CreditCard}
								updateError={updateSubscriptionError}
								updateLoading={updateSubscriptionLoading}
								update={update}
								useLinkedOrganisationData={useLinkedOrganisationData}
								allowedPlanCategories={allowedPlanCategories}
							/>
						) : (
							<DeskTopOptions
								id={id}
								paymentMethod={PaymentMethod.CreditCard}
								updateError={updateSubscriptionError}
								updateLoading={updateSubscriptionLoading}
								update={update}
								useLinkedOrganisationData={useLinkedOrganisationData}
								allowedPlanCategories={allowedPlanCategories}
							/>
						)}
					</Flex>
				</FlexColumnSpaced>
			</Padded>
		</Paper>
	);
};

export function CreditCardList({
	provider,
	showProviderName,
	useLinkedOrganisationData,
	allowedPlanCategories
}: {
	provider: ProviderSelection;
	showProviderName: boolean;
	useLinkedOrganisationData: boolean;
	allowedPlanCategories?: string[];
}) {
	const { getCustomers, getSubscriptions } = useFinancialContext();
	const customer = getCustomers(useLinkedOrganisationData);
	const subscriptions = getSubscriptions(useLinkedOrganisationData, allowedPlanCategories);
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState<string[]>(
		subscriptions?.length === 1 ? [subscriptions[0].id] : []
	);
	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => render());

	const { requestToken, contentId } = useContentContext();
	const {
		error: updateSubscriptionError,
		loading: updateSubscriptionLoading,
		mutate: updateSubsciption
	} = useUpdateSubscriptionPaymentMethodMutation({ requestToken });
	const PaymentAdd = getComponentExtension<PaymentAddProps>(
		paymentExtension(provider.providerExtension, convertMemberPortalToSfMethodEnumValue(provider.method), 'add')
	);

	if (!PaymentAdd) return null;
	return (
		<FlexColumnSpaced key={`${provider.name}_${provider.method}`}>
			{customer
				.flatMap(c => c.creditCards)
				?.filter(c => {
					return c.provider === provider.provider;
				})
				?.map(pm => {
					return (
						<CreditCardDisplayWithOptions
							key={pm.id}
							card={pm}
							provider={provider}
							useLinkedOrganisationData={useLinkedOrganisationData}
							allowedPlanCategories={allowedPlanCategories}
						/>
					);
				})}
			{updateSubscriptionError ? <MuiAlert severity="error">{updateSubscriptionError}</MuiAlert> : null}
			{isOpened ? (
				<Spin spinning={updateSubscriptionLoading}>
					<PaymentAdd
						onSuccess={async (id: string) => {
							for (const subscriptionId of selectedSubscriptionIds) {
								await updateSubsciption({
									id: subscriptionId,
									contentId,
									method: provider.method,
									provider: provider.provider,
									creditCardId: id
								});
							}
							setIsOpened(false);
							sendUpdate(REFETCH_FINANCIAL);
						}}
						onCancel={() => setIsOpened(false)}
						provider={provider}
						settings={tryParseJSONObject(provider.settings)}
						showSave
						meta={{
							useLinkedOrganisationData: useLinkedOrganisationData
						}}
					/>
					<SubscriptionSelector
						selectedSubscriptionIds={selectedSubscriptionIds}
						setSelectedSubscriptionIds={setSelectedSubscriptionIds}
						subscriptions={subscriptions}
					/>
				</Spin>
			) : (
				<Button variant="tertiary" color="secondary" startIconName="Add" onClick={() => setIsOpened(true)}>
					{showProviderName ? `Add Credit Card - ${provider.name}` : 'Add Credit Card'}
				</Button>
			)}
		</FlexColumnSpaced>
	);
}
